/**
 * Import the team's "Daily Update" Word file — port of the original importDocx(), used by the Daily Report and Settings.
 * Reads `word/document.xml` with JSZip (loaded only when needed), parses it with domain/docxImport and opens a preview;
 * nothing is saved until the user confirms in the preview.
 */
import { useQueryClient } from '@tanstack/react-query';
import type { Member, ParsedDay, Project } from '@/types/models';
import { docxParagraphs, parseDailyDoc, summarizeImport } from '@/domain/docxImport';
import { qk } from '@/hooks/data';
import { useModal, useServices, useToast } from '@/app/AppContext';
import { DocxImportModal } from './DocxImportModal';

type ZipLib = typeof import('jszip');

async function readDays(JSZip: ZipLib, file: File, known: { projects: Project[]; members: Member[] }): Promise<ParsedDay[]> {
  const zip = await JSZip.loadAsync(file);
  const doc = zip.file('word/document.xml');
  if (!doc) throw new Error('word/document.xml is missing.');
  return parseDailyDoc(docxParagraphs(await doc.async('string')), known);
}

export function useDocxImport(): (file: File) => Promise<void> {
  const s = useServices();
  const qc = useQueryClient();
  const toast = useToast();
  const modal = useModal();
  return async (file: File) => {
    // Known project and member names help the parser recognise headings and people (cached lists; fetched when missing).
    const lists = await Promise.all([
      qc.ensureQueryData({ queryKey: qk.projects, queryFn: () => s.projects.list() }),
      qc.ensureQueryData({ queryKey: qk.members, queryFn: () => s.members.list() }),
    ]).catch((e: unknown) => {
      toast(e instanceof Error ? e.message : 'Something went wrong. Please try again.', true);
      return null;
    });
    if (!lists) return;
    const [projects, members] = lists;
    const JSZip = await import('jszip').then(
      (m) => m.default,
      () => null,
    );
    if (!JSZip) {
      toast('Could not load the .docx reader. Check the internet connection.', true);
      return;
    }
    const days = await readDays(JSZip, file, { projects, members }).catch(() => null);
    if (!days) {
      toast('That file could not be read as a Word document.', true);
      return;
    }
    if (!days.length) {
      toast('No "Daily Update - <date>" sections were found in this file.', true);
      return;
    }
    const summary = summarizeImport(days, projects, members);
    modal.open((close) => <DocxImportModal fileName={file.name} days={days} summary={summary} onClose={close} />, { wide: true });
  };
}
